import { ViewDefinition } from '../../board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

export interface CustomViewDto {
  viewId?: number;
  applicationId: number;
  viewName: string;
  viewDescription?: string;
  viewDefinition: string; // JSON string of ViewDefinition
  isActive: boolean;
  isShared: boolean;
  displayOrder?: number;
  createdBy?: string;
  createdDate?: Date;
  modifiedBy?: string;
  modifiedDate?: Date;
}

export interface CustomViewsDto {
  customViews: CustomViewDto[];
}

export interface CustomViewListItem {
  viewId: number;
  viewName: string;
  viewDescription?: string;
  isActive: boolean;
  isShared: boolean;
  displayOrder?: number;
  createdBy: string;
  modifiedDate: Date;
}

export class CustomViewIdRequest {
  viewId: number;

  constructor(viewId: number) {
    this.viewId = viewId;
  }
}

export class ApplicationIdRequest {
  applicationId: number;

  constructor(applicationId: number) {
    this.applicationId = applicationId;
  }
}


/**
 * Convert DTO to ViewDefinition
 */
export function convertCustomViewDtoToModel(dto: CustomViewDto): ViewDefinition {
  try {
    const viewDef = JSON.parse(dto.viewDefinition) as ViewDefinition;

    // Ensure all required properties are present
    if (!viewDef.id) {
      viewDef.id = dto.viewId?.toString() || generateViewId();
    }

    // Ensure viewManager exists with proper structure
    // Note: contextData is runtime state and should always start empty
    if (!viewDef.viewManager) {
      viewDef.viewManager = {
        contextData: {},
        contextInstructions: {},
        globalFilters: {},
      };
    } else {
      // contextData is runtime state - always initialize as empty, never restore from saved data
      viewDef.viewManager.contextData = {};
      viewDef.viewManager.globalFilters = viewDef.viewManager.globalFilters || {};
    }

    // Ensure tabs array exists
    viewDef.tabs = viewDef.tabs || [];

    // Ensure each tab has proper structure
    viewDef.tabs.forEach(tab => {
      tab.cells = tab.cells || [];
      tab.canvas = tab.canvas || { width: 1200, height: 800 };
      tab.tabType = tab.tabType || 'standard'; // Default to standard for backward compatibility

      // Ensure each cell has proper structure
      tab.cells.forEach(cell => {
        cell.configuration = cell.configuration || {};
        cell.dataBinding = cell.dataBinding || {};
        cell.position = cell.position || { x: 0, y: 0, width: 100, height: 100 };
      });

      // Ensure repeating sections have proper structure
      if (tab.repeatingSections) {
        tab.repeatingSections.forEach(section => {
          section.cells = section.cells || [];
          section.cells.forEach(cell => {
            cell.configuration = cell.configuration || {};
            cell.dataBinding = cell.dataBinding || {};
            cell.position = cell.position || { x: 0, y: 0, width: 100, height: 100 };
          });
        });
      }
    });

    // Set metadata from DTO
    viewDef.name = dto.viewName;
    viewDef.description = dto.viewDescription || '';
    viewDef.isActive = dto.isActive;
    viewDef.isShared = dto.isShared;
    viewDef.displayOrder = dto.displayOrder;
    viewDef.createdBy = dto.createdBy || 'Unknown';
    viewDef.lastModified = dto.modifiedDate || new Date();

    return viewDef;
  } catch (error) {
    console.error('Error parsing view definition:', error);
    throw new Error('Invalid view definition format');
  }
}

/**
 * Strip transient runtime properties (prefixed with _) from cell configuration.
 * These are used for in-memory state like saved filter rows and should not be persisted.
 */
const TRANSIENT_CONFIG_KEYS = new Set([
  '_activeFilterState',
  '_activeFilterHasActive',
  '_lastFilterSignature',
  '_isPdfEditAddition',
  '_pdfAbsoluteY',
]);

function cleanCellConfiguration(config: any): any {
  if (!config || typeof config !== 'object') return config || {};
  const clean: any = {};
  for (const key of Object.keys(config)) {
    if (!TRANSIENT_CONFIG_KEYS.has(key)) {
      clean[key] = config[key];
    }
  }
  return clean;
}

/**
 * Convert ViewDefinition to DTO
 */
export function convertCustomViewModelToDto(applicationId: number, viewDef: ViewDefinition, viewId?: number): CustomViewDto {
  // Create a clean copy for serialization
  // Note: contextData is runtime state and should NOT be persisted
  const cleanViewDef = {
    id: viewDef.id,
    name: viewDef.name,
    description: viewDef.description,
    viewManager: {
      contextInstructions: viewDef.viewManager.contextInstructions || {},
      globalFilters: viewDef.viewManager.globalFilters || {},
    },
    tabs: viewDef.tabs.map(tab => ({
      id: tab.id,
      name: tab.name,
      order: tab.order,
      tabType: tab.tabType || 'standard',
      canvas: {
        width: tab.canvas.width,
        height: tab.canvas.height
      },
      cells: tab.cells.map(cell => ({
        id: cell.id,
        type: cell.type,
        position: {
          x: cell.position.x,
          y: cell.position.y,
          width: cell.position.width,
          height: cell.position.height,
          zIndex: cell.position.zIndex
        },
        configuration: cleanCellConfiguration(cell.configuration),
        dataBinding: cell.dataBinding || {}
      })),
      ...(tab.pdfConfig && { pdfConfig: tab.pdfConfig }),
      ...(tab.requiredContexts && tab.requiredContexts.length > 0 && { requiredContexts: tab.requiredContexts }),
      ...(tab.repeatingSections && tab.repeatingSections.length > 0 && {
        repeatingSections: tab.repeatingSections.map(section => ({
          id: section.id,
          y: section.y,
          height: section.height,
          dataSource: section.dataSource,
          ...(section.datasetId && { datasetId: section.datasetId }),
          ...(section.contextVariable && { contextVariable: section.contextVariable }),
          ...(section.cellFilters && section.cellFilters.length > 0 && { cellFilters: section.cellFilters }),
          cells: section.cells.map(cell => ({
            id: cell.id,
            type: cell.type,
            position: {
              x: cell.position.x,
              y: cell.position.y,
              width: cell.position.width,
              height: cell.position.height,
              zIndex: cell.position.zIndex
            },
            configuration: cell.configuration || {},
            dataBinding: cell.dataBinding || {}
          })),
          ...(section.rowBorder !== undefined && { rowBorder: section.rowBorder }),
          ...(section.alternateRowColor && { alternateRowColor: section.alternateRowColor }),
          ...(section.storeContextVariable && { storeContextVariable: section.storeContextVariable })
        }))
      }),
      ...(tab.keepTogetherBlocks && tab.keepTogetherBlocks.length > 0 && {
        keepTogetherBlocks: tab.keepTogetherBlocks.map(block => ({
          id: block.id,
          y: block.y,
          height: block.height,
          memberCellIds: block.memberCellIds || []
        }))
      })
    })),
    activeTabId: viewDef.activeTabId,
    isActive: viewDef.isActive,
    isShared: viewDef.isShared,
    createdBy: viewDef.createdBy,
    lastModified: viewDef.lastModified
  };

  return {
    viewId: viewId,
    applicationId: applicationId,
    viewName: viewDef.name,
    viewDescription: viewDef.description,
    viewDefinition: JSON.stringify(cleanViewDef),
    isActive: viewDef.isActive,
    isShared: viewDef.isShared,
    displayOrder: viewDef.displayOrder,
    createdBy: viewDef.createdBy,
    modifiedDate: viewDef.lastModified
  };
}

/**
 * Generate a unique ID for new views
 */
export function generateViewId(): string {
  return `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validate a view definition has all required fields
 */
export function validateViewDefinition(viewDef: ViewDefinition): boolean {
  if (!viewDef.name || !viewDef.tabs || viewDef.tabs.length === 0) {
    return false;
  }

  // Validate each tab
  for (const tab of viewDef.tabs) {
    if (!tab.id || !tab.name || !tab.canvas) {
      return false;
    }

    // Validate each cell
    for (const cell of tab.cells) {
      if (!cell.id || !cell.type || !cell.position) {
        return false;
      }
    }

    // Validate repeating section cells
    if (tab.repeatingSections) {
      for (const section of tab.repeatingSections) {
        if (!section.id || !section.dataSource) {
          return false;
        }
        for (const cell of section.cells) {
          if (!cell.id || !cell.type || !cell.position) {
            return false;
          }
        }
      }
    }
  }

  return true;
}
